import React from 'react';
import { OptionContract } from '../../types';

interface ContractComparisonProps {
  contracts: OptionContract[];
  currentPrice: number;
  daysToExpiration: number;
  onClose: () => void;
}

const ContractComparison: React.FC<ContractComparisonProps> = ({
  contracts,
  currentPrice,
  daysToExpiration,
  onClose
}) => {
  // Calculate metrics for each contract
  const contractMetrics = contracts.map(option => {
    // Premium = the option mark (last trade); there is no quote feed for a real mid.
    const midPrice = option.last_price ?? 0;
    const maxProfitPercent = currentPrice > 0 ? (midPrice / currentPrice) * 100 : 0;
    const headroom = currentPrice > 0 ? ((option.strike - currentPrice) / currentPrice) * 100 : 0;
    const annualYield = currentPrice > 0 && daysToExpiration > 0
      ? (midPrice / currentPrice) * (365 / daysToExpiration) * 100
      : 0;
    // Real contract spread only (never from the estimated bid/ask); unknown counts as 0 here.
    const bidAskSpread = option.bid_ask_spread_pct ?? 0;

    // Liquidity level
    const getLiquidityLevel = (openInterest: number, spread: number) => {
      if (openInterest > 1000 && spread < 5) return { label: 'High', color: '#10B981', score: 3 };
      if (openInterest > 100 && spread < 10) return { label: 'Medium', color: '#F59E0B', score: 2 };
      return { label: 'Low', color: '#EF4444', score: 1 };
    };

    // Assignment risk level
    const getAssignmentRiskLevel = (delta: number) => {
      if (delta < 0.25) return { label: 'Low', color: '#10B981', score: 3 };
      if (delta < 0.35) return { label: 'Medium', color: '#F59E0B', score: 2 };
      return { label: 'High', color: '#EF4444', score: 1 };
    };

    const liquidityLevel = getLiquidityLevel(option.open_interest || 0, bidAskSpread);
    const assignmentRisk = getAssignmentRiskLevel(option.delta || 0);

    return {
      option,
      midPrice,
      maxProfitPercent,
      headroom,
      annualYield,
      liquidityLevel,
      assignmentRisk,
      bidAskSpread
    };
  });

  // Determine highest/lowest for each metric
  const getHighLowIndicators = (metric: keyof typeof contractMetrics[0], higherIsBetter: boolean = true) => {
    const values = contractMetrics.map(c => {
      const value = c[metric];
      if (typeof value === 'object' && 'score' in value) {
        return (value as any).score;
      }
      return value as number;
    });

    const highValue = higherIsBetter ? Math.max(...values) : Math.min(...values);
    const lowValue = higherIsBetter ? Math.min(...values) : Math.max(...values);

    return contractMetrics.map(c => {
      const value = typeof c[metric] === 'object' && 'score' in (c[metric] as any)
        ? (c[metric] as any).score
        : c[metric] as number;

      if (value === highValue && highValue !== lowValue) return 'highest';
      if (value === lowValue && highValue !== lowValue) return 'lowest';
      return 'neutral';
    });
  };

  const yieldIndicators = getHighLowIndicators('annualYield', true);
  const liquidityIndicators = getHighLowIndicators('liquidityLevel', true);
  const riskIndicators = getHighLowIndicators('assignmentRisk', true); // Higher score = lower risk

  // Get contract summary for each contract
  const getContractRecommendation = (index: number) => {
    const contract = contractMetrics[index];

    // Determine primary characteristic
    let primaryStrength = '';
    let description = '';

    if (yieldIndicators[index] === 'highest') {
      primaryStrength = `**Highest Income:** ${contract.annualYield.toFixed(1)}% annual yield`;
      description = 'This contract has the highest yield among those selected.';
    } else if (riskIndicators[index] === 'highest') {
      primaryStrength = `**Lowest Assignment Probability:** ${contract.assignmentRisk.label}`;
      description = 'This contract has the lowest assignment probability among those selected.';
    } else if (liquidityIndicators[index] === 'highest') {
      primaryStrength = `**Highest Liquidity:** ${contract.liquidityLevel.label} trading flexibility`;
      description = 'This contract has the highest liquidity among those selected.';
    } else {
      // For contracts that aren't highest in any category, find their relative characteristics
      const avgYield = contractMetrics.reduce((sum, c) => sum + c.annualYield, 0) / contractMetrics.length;

      if (yieldIndicators[index] !== 'lowest' && riskIndicators[index] !== 'lowest' && liquidityIndicators[index] !== 'lowest') {
        primaryStrength = `**Balanced Profile:** Moderate across all metrics`;
        description = 'This contract falls in the middle range for yield, risk, and liquidity.';
      } else if (contract.annualYield >= avgYield && riskIndicators[index] !== 'lowest') {
        primaryStrength = `**Moderate Income:** ${contract.annualYield.toFixed(1)}% yield`;
        description = 'This contract offers above-average yield with moderate risk.';
      } else if (contract.assignmentRisk.score >= 2) {
        primaryStrength = `**Moderate Risk:** ${contract.assignmentRisk.label} assignment probability`;
        description = 'This contract has moderate assignment probability.';
      } else {
        primaryStrength = `**Alternative Option:** ${contract.annualYield.toFixed(1)}% annual yield`;
        description = 'Review based on your specific criteria and preferences.';
      }
    }

    return `${primaryStrength}\n\n${description}`;
  };

  // Check if mobile
  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: isMobile ? '100%' : '95vw',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #333'
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '20px 16px' : '24px 32px',
          borderBottom: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              marginBottom: '8px'
            }}>
              Contract Comparison
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#888',
              margin: 0
            }}>
              Compare {contracts.length} selected option contracts side by side
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #555',
              borderRadius: '6px',
              color: '#ccc',
              fontSize: '16px',
              padding: '8px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#333';
              e.currentTarget.style.borderColor = '#777';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#555';
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: isMobile ? '16px' : '0 32px 32px' }}>

          {/* Comparison Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : contracts.length <= 2
                ? `repeat(${contracts.length}, 1fr)`
                : `repeat(auto-fit, minmax(280px, 1fr))`,
            gap: '16px'
          }}>
            {contractMetrics.map((contract, index) => {
              const getIndicatorIcon = (indicators: string[], index: number) => {
                if (indicators[index] === 'highest') return { icon: '↑', color: '#10B981' };
                if (indicators[index] === 'lowest') return { icon: '↓', color: '#EF4444' };
                return { icon: '−', color: '#9CA3AF' };
              };

              return (
                <div key={contract.option.strike} style={{
                  backgroundColor: '#2a2a2a',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #444',
                  minWidth: isMobile ? 'auto' : '280px'
                }}>
                  {/* Header */}
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '16px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid #444'
                  }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#fff',
                      marginBottom: '4px'
                    }}>
                      Strike: ${contract.option.strike}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: contract.headroom > 0 ? '#10B981' : '#EF4444'
                    }}>
                      {contract.headroom.toFixed(1)}% Headroom
                    </div>
                  </div>

                  {/* Metrics */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Premium */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#9CA3AF', marginBottom: '4px' }}>
                        Premium
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>
                        ${contract.midPrice.toFixed(2)}
                      </div>
                    </div>

                    {/* Annual Yield */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>Annual Yield</span>
                        <span style={{
                          fontSize: '12px',
                          color: getIndicatorIcon(yieldIndicators, index).color
                        }}>
                          {getIndicatorIcon(yieldIndicators, index).icon}
                        </span>
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#10B981' }}>
                        {contract.annualYield.toFixed(1)}%
                      </div>
                    </div>

                    {/* Assignment Risk */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>Assignment Risk</span>
                        <span style={{
                          fontSize: '12px',
                          color: getIndicatorIcon(riskIndicators, index).color
                        }}>
                          {getIndicatorIcon(riskIndicators, index).icon}
                        </span>
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: contract.assignmentRisk.color
                      }}>
                        {contract.assignmentRisk.label}
                      </div>
                    </div>

                    {/* Liquidity */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>Liquidity</span>
                        <span style={{
                          fontSize: '12px',
                          color: getIndicatorIcon(liquidityIndicators, index).color
                        }}>
                          {getIndicatorIcon(liquidityIndicators, index).icon}
                        </span>
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: contract.liquidityLevel.color
                      }}>
                        {contract.liquidityLevel.label}
                      </div>
                    </div>
                  </div>

                  {/* Professional Recommendation */}
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '12px',
                    borderTop: '1px solid #444',
                    fontSize: '13px',
                    color: '#E5E7EB',
                    lineHeight: 1.5,
                    textAlign: 'left'
                  }}>
                    {getContractRecommendation(index).split('\n\n').map((paragraph, pIndex) => (
                      <div key={pIndex} style={{
                        marginBottom: pIndex === 0 ? '8px' : '0',
                        fontWeight: pIndex === 0 ? '600' : '400',
                        color: pIndex === 0 ? '#F3F4F6' : '#D1D5DB'
                      }}>
                        {paragraph.includes('**') ? (
                          <>
                            {paragraph.split('**')[0]}
                            <strong style={{ color: '#60A5FA' }}>
                              {paragraph.split('**')[1]}
                            </strong>
                            {paragraph.split('**')[2]}
                          </>
                        ) : (
                          paragraph
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContractComparison;